import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { usePostHog } from '@posthog/react'

import { selectCurrentUser } from '@/app/slices/appSlice'
import { selectOrganisation } from '@/app/slices/organisationSlice'

/**
 * usePostHogIdentity - Keeps PostHog's person and organisation-group identity in sync with
 * the logged-in user and their active organisation.
 *
 * This is the PostHog equivalent of the two Harness FME clients in useToolBoxTreatments: the
 * default client keyed on the user's email, and the second organisationId-keyed client used
 * only for ToolBox_RenameConsumptionModes. PostHog's group() covers both with one client, so a
 * flag can target by person or by organisation without a second subscription.
 *
 * Mount once, near the root (SplitProviderWrapper, the element every route renders under), so
 * identity resolves before any flag-gated component reads useFeatureFlags.
 */
export function usePostHogIdentity() {
    const posthog = usePostHog()
    const currentUser = useSelector(selectCurrentUser)
    const organisation = useSelector(selectOrganisation)

    useEffect(() => {
        if (!currentUser?.userId) return

        // Stable internal id with email/name as properties, mirroring initUserFlow
        // (UserFlow.js) rather than keying on email the way the FME client does today — this is
        // the identity shape flags will target once they migrate off FME.
        posthog.identify(currentUser.userId, {
            email: currentUser.emailAddress,
            name: [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' '),
        })

        // Properties set via identify() are only usable for flag targeting after ingestion;
        // setPersonPropertiesForFlags sends them with the flag request itself, so targeting
        // works on this person's very first page load — the analogue of FME's `attributes`.
        posthog.setPersonPropertiesForFlags({ emailAddress: currentUser.emailAddress })
    }, [posthog, currentUser?.userId, currentUser?.emailAddress, currentUser?.firstName, currentUser?.lastName])

    useEffect(() => {
        if (!organisation?.organisationId) return

        // Keyed on organisationId, so switching organisations re-fires this and PostHog reloads
        // flags for the new group automatically.
        posthog.group('organisation', organisation.organisationId, {
            // The group key isn't filterable in PostHog release conditions, only properties are —
            // duplicate it here so flags can target an exact organisation by id.
            organisationId: organisation.organisationId,
            name: organisation.name,
            createdAt: organisation.createDateUtc,
            paymentGatewayCustomerId: organisation.paymentGatewayCustomerId,
        })
    }, [
        posthog,
        organisation?.organisationId,
        organisation?.name,
        organisation?.createDateUtc,
        organisation?.paymentGatewayCustomerId,
    ])
}
